import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { message, Modal, Popover } from "antd";
import {
  BarChartOutlined,
  ClearOutlined,
  DashboardOutlined,
  EditOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  HomeOutlined,
  LineChartOutlined,
  LogoutOutlined,
  MoreOutlined,
  PictureOutlined,
  RadarChartOutlined,
  ReconciliationOutlined,
  RiseOutlined,
  RocketOutlined,
  ScanOutlined,
  UserOutlined,
} from "@ant-design/icons";
import ProfileModalContent from "./ProfileModalContent";
import PasswordModalContent from "./PasswordModalContent";
import { useLocalStorage } from "../hooks";
import { logoutUser } from "../utils/api";
import { UserContext } from "../context/UserContext";
import { checkOrgHasReports } from "../consts/supabaseClient";
import {
  showPipelineLogsTab,
  showVisualisationDashboard,
} from "../consts/config";
import PERMISSIONS from "../consts/permissions";
import { useMemo } from "react";
const Sidebar = ({ active, open, setOpen, sidebarType = "default" }) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [accessToken, setAccessToken] = useLocalStorage("accessToken", null);
  const [refreshToken, setRefreshToken] = useLocalStorage("refreshToken", null);
  const { userData } = useContext(UserContext);
  const [defaultDropdown] = useLocalStorage("dropdowns", null);
  const openClassesIcon = "justify-start px-2";
  const closedClassesIcon = "justify-center";
  const activeClassesIcon = "text-primaryBlue";

  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [showReportsEnabled, setShowReportsEnabled] = useState(false);

  const currentOrg = defaultDropdown?.org;

  const checkPermissions = (requiredPermissions) => {
    if (userData?.is_superuser) return true;
    const userPermissions = new Set(userData.permissions.map((p) => p.name));
    return requiredPermissions.some((perm) => userPermissions.has(perm));
  };

  useEffect(() => {
    if (!currentOrg) {
      setShowReportsEnabled(false);
      return;
    }

    checkOrgHasReports(currentOrg).then(setShowReportsEnabled);
  }, [currentOrg]);

  const showDashboardTab = () => {
    if (userData?.organization.is_grower && sidebarType === "TomatoGrower") {
      const requiredPermissions = [
        PERMISSIONS.HARVEST_SCHEDULE.VIEW,
        PERMISSIONS.HARVEST_SCHEDULE.EDIT,
      ];
      return checkPermissions(requiredPermissions);
    }
    return true;
  };
  const showScanningSessionTab = () => {
    if (userData?.organization.is_grower && sidebarType === "TomatoGrower") {
      const requiredPermissions = [
        PERMISSIONS.SCANNING_SESSIONS.VIEW,
        PERMISSIONS.SCANNING_SESSIONS.EDIT,
      ];
      return checkPermissions(requiredPermissions);
    }
    return true;
  };
  const showReportingTab = () => {
    if (userData?.organization.is_grower && sidebarType === "TomatoGrower") {
      const requiredPermissions = [
        PERMISSIONS.REPORTING.VIEW,
        PERMISSIONS.REPORTING.EDIT,
      ];
      return checkPermissions(requiredPermissions);
    }
    return true;
  };

  const BASE_SIDEBAR_TABS = [
    {
      id: "logs",
      text: "Logs",
      icon: <RocketOutlined />,
      url: "/logs",
      visible: userData?.organization.is_pollination,
    },
    {
      id: "visualisation",
      text: "Visualisation",
      icon: <PictureOutlined />,
      url: "/visualisation-dashboard",
      visible:
        userData?.organization.is_grower &&
        showVisualisationDashboard &&
        userData?.is_superuser &&
        sidebarType !== "TomatoGrower",
    },
    {
      id: "pipelineLogs",
      text: "Pipeline Logs",
      icon: <RocketOutlined />,
      url: "/pipeline-logs",
      visible: userData?.organization.is_grower && showPipelineLogsTab,
    },
    {
      id: "reports",
      text: "Reports",
      icon: <FileTextOutlined />,
      url: "/reports",
      visible: showReportsEnabled,
    },
    {
      id: "admin",
      text: "Admin",
      icon: <UserOutlined />,
      url: "/admin",
      visible:
        userData?.is_admin ||
        userData?.is_superuser ||
        userData?.site_admins.length > 0,
    },
  ];

  const SIDEBAR_TABS = [
    {
      id: "home",
      // text: `${userData?.organization.is_breeder ? 'Home' : 'Forecast'}`,
      text: `${
        userData?.organization.is_breeder
          ? "Home"
          : sidebarType === "TomatoGrower"
            ? "Harvest Schedule"
            : sidebarType === "SpinachGrower" ||
                sidebarType === "BroccoliGrower"
              ? "Forecast"
              : "Home"
      }`,
      icon:
        sidebarType === "TomatoGrower" ? (
          <ClearOutlined />
        ) : sidebarType === "SpinachGrower" ||
          sidebarType === "BroccoliGrower" ? (
          <LineChartOutlined />
        ) : (
          <HomeOutlined />
        ),
      url: "/dashboard",
      visible: showDashboardTab(),
    },
    {
      id: "spinach_harvest_optimizer",
      text: "Harvest Optimizer",
      icon: <DashboardOutlined />,
      url: "/spinach/harvest-optimiser",
      visible:
        userData?.organization.is_grower &&
        sidebarType === "SpinachGrower",
    },
    {
      id: "broccoli_harvest_optimizer",
      text: "Harvest Optimizer",
      icon: <DashboardOutlined />,
      url: "/broccoli/harvest-optimiser",
      visible:
        userData?.organization.is_grower &&
        sidebarType === "BroccoliGrower",
    },
    {
      id: "broccoli_harvest_entry",
      text: "Harvest Entry",
      icon: <FileDoneOutlined />,
      url: "/broccoli/harvest-entry",
      visible:
        userData?.organization.is_grower &&
        sidebarType === "BroccoliGrower",
    },
    {
      id: "spinach_harvest_entry",
      text: "Harvest Entry",
      icon: <FileDoneOutlined />,
      url: "/spinach/harvest-entry",
      visible:
        userData?.organization.is_grower &&
        sidebarType === "SpinachGrower",
    },
    {
      id: "broccoli_analysis",
      text: "Analysis",
      icon: <BarChartOutlined />,
      url: "/broccoli/analysis",
      visible:
        userData?.organization.is_grower && sidebarType === "BroccoliGrower",
    },
    {
      id: "spinach_analysis",
      text: "Analysis",
      icon: <BarChartOutlined />,
      url: "/spinach/analysis",
      visible:
        userData?.organization.is_grower && sidebarType === "SpinachGrower",
    },
    {
      id: "scanning_sessions",
      text: "Scanning Sessions",
      icon: <ScanOutlined />,
      url: "/scanning-sessions",
      visible:
        userData?.organization.is_grower &&
        sidebarType === "TomatoGrower" &&
        showScanningSessionTab(),
    },
    {
      id: "reporting",
      text: "Reporting",
      icon: <ReconciliationOutlined />,
      url: "/reporting",
      visible:
        userData?.organization.is_grower &&
        sidebarType === "TomatoGrower" &&
        showReportingTab(),
    },
    {
      id: "plots",
      text: "Plots",
      icon: <RiseOutlined />,
      url: "/plots",
      visible: userData?.organization.is_breeder,
    },
    {
      id: "analysis",
      text: "Analysis",
      icon: <RadarChartOutlined />,
      url: "/analysis",
      visible: userData?.organization.is_breeder,
    },
  ];

  const computedTabs = useMemo(() => {
    return [...SIDEBAR_TABS, ...BASE_SIDEBAR_TABS];
  }, [SIDEBAR_TABS, BASE_SIDEBAR_TABS, userData, sidebarType]);

  useEffect(() => {
    setName(`${userData?.first_name} ${userData?.last_name}`);
    setEmail(userData?.email);
  }, [userData]);

  async function purgePWA() {
    try {
      // delete Workbox caches
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      // unregister all SWs
      if (navigator.serviceWorker) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      // wipe IndexedDB
      if (indexedDB && indexedDB.databases) {
        const dbs = await indexedDB.databases();
        await Promise.all(
          dbs.map((db) =>
            db?.name
              ? new Promise((res) => {
                  const req = indexedDB.deleteDatabase(db.name);
                  req.onsuccess = req.onerror = req.onblocked = () => res();
                })
              : Promise.resolve(),
          ),
        );
      }
    } catch (_) {
      // swallow; logout must not be blocked
      console.log("Failed to remove database", _);
    }
  }

  const logout = async () => {
    setAccessToken(null);
    setRefreshToken(null);
    try {
      await logoutUser();
    } catch (err) {
      const msg = err?.response?.data?.message || "Oops! Something went wrong!";
      messageApi.open({
        type: "error",
        content: msg,
      });
    } finally {
      window.localStorage.clear();
      sessionStorage.clear();
      await purgePWA();
      window.location.replace(`${window.location.origin}`);
    }
  };

  const showProfileModal = () => {
    setProfileModalOpen(true);
  };
  const handleProfileCancel = () => {
    setProfileModalOpen(false);
  };

  const showPasswordModal = () => {
    setPasswordModalOpen(true);
  };
  const handlePasswordCancel = () => {
    setPasswordModalOpen(false);
  };

  const toggleState = (val) => {
    setOpen(val);
  };

  const MoreOptionsPopover = () => (
    <div className="flex flex-col items-center font-MuseoSans">
      <div
        onClick={showProfileModal}
        className={`w-full flex items-center rounded text-xl px-2 py-2 hover:bg-gray hover:cursor-pointer`}
      >
        <EditOutlined />
        <p className="ml-2 text-base">Edit Profile</p>
      </div>
      <div
        onClick={showPasswordModal}
        className={`w-full flex items-center rounded text-xl px-2 py-2 hover:bg-gray hover:cursor-pointer`}
      >
        <EditOutlined />
        <p className="ml-2 text-base">Change Password</p>
      </div>
      <div
        onClick={logout}
        className={`w-full flex items-center rounded text-xl px-2 py-2 hover:bg-gray hover:cursor-pointer`}
      >
        <LogoutOutlined />
        <p className="ml-2 text-base">Logout</p>
      </div>
    </div>
  );

  return (
    <div
      id="sidebar"
      className={`bg-white fixed font-MuseoSans h-screen ${
        open ? "w-[210px]" : "w-[50px]"
      } py-5 shadow-md flex flex-col justify-between transition-height ease-in-out duration-300 overflow-auto z-100`}
      onMouseOver={() => toggleState(true)}
      onMouseOut={() => toggleState(false)}
    >
      {contextHolder}
      <div className="px-3">
        <img
          className="mb-5 h-6 w-full"
          src={`${
            open
              ? "/images/phenobee_logo_blue.svg"
              : "/images/phenobee_logo_icon_blue.svg"
          }`}
          alt="Polybee Logo"
        />

        {computedTabs.map((data) => {
          return data.visible ? (
            <Link to={data.url} key={data.id}>
              <div
                key={data.id}
                className={`flex items-center ${
                  open ? openClassesIcon : closedClassesIcon
                } ${
                  data.id === active ? activeClassesIcon : ""
                } rounded text-2xl py-2 hover:bg-gray hover:cursor-pointer`}
              >
                {data.icon}
                <>{open && <p className="ml-2 text-base">{data.text}</p>}</>
              </div>
            </Link>
          ) : null;
        })}
      </div>
      <div>
        {userData ? (
          // <div
          //   className={`flex items-center  mb-5 ${
          //     open ? 'px-5 justify-start' : 'justify-center'
          //   }`}
          <div className={`flex items-center  mb-5 justify-center`}>
            <img
              className="h-10"
              src={userData?.organization?.logo}
              alt={userData?.organization?.name || ""}
            />
            {/*{open && (*/}
            {/*  <p className='ml-2 text-lg'>*/}
            {/*    {userData?.organization?.name || ''}*/}
            {/*  </p>*/}
            {/*)}*/}
          </div>
        ) : null}
        <div className="h-[1px] flex items-center bg-gray my-4 px-2"></div>
        <div
          className={`w-full flex ${
            open ? "justify-between px-3" : "justify-center"
          } items-center py-2`}
        >
          <div className="text-xl flex items-center justify-center bg-gray rounded-full min-h-[36px] min-w-[36px]">
            {name[0]}
          </div>
          {open && (
            <Popover
              placement="top"
              title={<div className="text-center max-w-full">{name}</div>}
              content={
                <div className="text-center text-xs text-darkerGray max-w-full">
                  {email}
                </div>
              }
            >
              <div className="flex flex-col flex-wrap items-center justify-center mx-1 max-w-[60%] max-h-[36px] overflow-hidden">
                <div className="text-center text-sm">
                  {name.length > 15 ? `${name.slice(0, 12)}...` : name}
                </div>
                <div className="text-center text-xs text-darkerGray max-w-full">
                  {email.length > 15 ? `${email.slice(0, 12)}...` : email}
                </div>
              </div>
            </Popover>
          )}
          {open && (
            <Popover
              trigger="click"
              title={null}
              content={<MoreOptionsPopover />}
            >
              <div className="rounded text-lg cursor-pointer">
                <MoreOutlined />
              </div>
            </Popover>
          )}
        </div>
      </div>
      <Modal
        maskClosable={false}
        title={
          <div className="mb-5 font-museoSansBold text-2xl">Edit Profile</div>
        }
        open={profileModalOpen}
        onCancel={handleProfileCancel}
        footer={null}
      >
        <ProfileModalContent setOpen={setProfileModalOpen} />
      </Modal>
      <Modal
        maskClosable={false}
        title={
          <div className="mb-5 font-museoSansBold text-2xl">
            Change Password
          </div>
        }
        open={passwordModalOpen}
        onCancel={handlePasswordCancel}
        footer={null}
      >
        <PasswordModalContent setOpen={setPasswordModalOpen} />
      </Modal>
    </div>
  );
};

export default Sidebar;
